/*
 * Wizard Step 1 (U7 — migrated to Mantine): pick a Product + optional Flow / Format filters.
 *
 * The product list comes from `GET /products` (the existing products listing —
 * `ProductConfigResponse.product_key`), reused via the config `useProducts`
 * hook. An empty Flow means "no flow filter" (sent as none on the Step-2 fetch).
 *
 * The product picker stays a NATIVE `<select>` (Mantine `NativeSelect`) so the
 * value is keyboard- and `selectOptions`-driven exactly as before; the flow
 * filter is a plain `TextInput`.
 */

import { type KeyboardEvent } from "react";
import { Alert, NativeSelect, Stack, TextInput } from "@mantine/core";
import { EmptyState } from "../ui";
import { errorMessage } from "../../api/errors";
import { useProducts } from "../config/hooks";

export function Step1SelectProduct({
  productKey,
  flow,
  format,
  onProductChange,
  onFlowChange,
  onFormatChange,
  onEnterAdvance,
}: {
  productKey: string;
  flow: string;
  format: string;
  onProductChange: (key: string) => void;
  onFlowChange: (flow: string) => void;
  onFormatChange: (format: string) => void;
  /**
   * Press Enter in any field on this (first) step to advance to the next step.
   * Scoped to step 1 ONLY: the wizard wires its step-advance here, and the Stepper
   * mounts this content only while step 1 is active — so no other step gets it. The
   * advance itself is gated upstream (a product must be selected), so Enter with no
   * product is a no-op, exactly like the disabled "Next" button.
   */
  onEnterAdvance?: () => void;
}) {
  const productsQuery = useProducts();
  const products = productsQuery.data ?? [];

  if (productsQuery.isError) {
    return (
      <Alert color="red" variant="light" role="alert">
        Couldn't load products: {errorMessage(productsQuery.error)}
      </Alert>
    );
  }

  if (!productsQuery.isLoading && products.length === 0) {
    return (
      <EmptyState
        icon="◈"
        title="No products configured"
        description="Configure a product before launching a run."
      />
    );
  }

  const productData = [
    { value: "", label: "Select a product…" },
    ...products.map((p) => ({
      value: p.product_key,
      label: p.label ? `${p.label} (${p.product_key})` : p.product_key,
    })),
  ];

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    // Enter advances to the next step. Ignore a mid-IME-composition Enter (a
    // confirming keystroke, not a submit) and any modified Enter.
    if (
      e.key === "Enter" &&
      !e.nativeEvent.isComposing &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey
    ) {
      e.preventDefault();
      onEnterAdvance?.();
    }
  }

  return (
    <Stack gap="md" maw={520} onKeyDown={handleKeyDown}>
      <NativeSelect
        label="Product"
        required
        description="The product whose Ready-to-Launch tickets you'll select."
        value={productKey}
        disabled={productsQuery.isLoading}
        onChange={(e) => onProductChange(e.currentTarget.value)}
        data={productData}
      />

      <TextInput
        label="Flow filter"
        description="Optional — leave blank to include every flow."
        value={flow}
        placeholder="e.g. LFS"
        onChange={(e) => onFlowChange(e.currentTarget.value)}
      />

      <TextInput
        label="Format filter"
        description="Optional — leave blank to include every format."
        value={format}
        placeholder="e.g. IMG"
        onChange={(e) => onFormatChange(e.currentTarget.value)}
      />
    </Stack>
  );
}
